/**
 * The planned route of a public aircraft on Teslyn Cloud's staff maps (R160; docs/specs/public-routes.md §4;
 * decisions 765, 933, 934): a click on an Oz Runways or AvPlan aircraft asks the `traffic-route` function once (it
 * checks the caller is the organisation's staff and the aircraft is in its public traffic); a click on an airline
 * flight asks `aircraft-details` for its published schedule. The answer opens a popup with the domain's rows and draws
 * the route in the aircraft's colour, solid for a pilot's plan, dashed and labelled for an airline's schedule; closing
 * the popup removes it. Colours are tokens; text reaches the map only as text nodes. Clients never see public air
 * traffic, so never a route.
 */
import L from 'leaflet';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  aircraftRouteTarget,
  aircraftSourceRows,
  publicRouteAsk,
  publicRouteView,
  scheduleRoute,
  trackedAircraftLabel,
  type AircraftColours,
  type AircraftPosition,
  type FlightRoute,
  type PublicRoute,
  type PublicRouteResult,
  type PublicRouteSource,
  type PublicRouteView,
} from '@teslyn/domain';
import { colourOf, css, textNode } from './leaflet.ts';

/** Asks the route of one aircraft; null when it has none to ask (the organisation's own, an ADS-B non-airline). */
export type RouteFetcher = (
  a: AircraftPosition,
) => { readonly source: PublicRouteSource; readonly result: Promise<PublicRouteResult> } | null;

const problemOf = (e: unknown): string =>
  e instanceof Error && e.message
    ? e.message
    : 'Teslyn Cloud could not ask for the route. Try again shortly.';

/** The staff maps' asks, through Teslyn Cloud's functions. */
export function cloudRouteFetcher(sb: SupabaseClient, tenantId: string): RouteFetcher {
  return (a) => {
    const ask = publicRouteAsk(a);
    if (!ask) return null;
    if (ask.source === 'airline') {
      const result = sb.functions
        .invoke<{ routes?: FlightRoute[] }>('aircraft-details', {
          body: { tenant_id: tenantId, callsigns: [ask.callsign] },
        })
        .then(({ data, error }): PublicRouteResult => {
          const r = data?.routes?.[0];
          if (error || !r) return { route: null, problem: null };
          return { route: scheduleRoute(r), problem: null };
        })
        .catch((): PublicRouteResult => ({ route: null, problem: null }));
      return { source: 'airline', result };
    }
    // the id the organisation's public traffic keeps for the aircraft (its row's source_id)
    const id = ask.source === 'ozrunways' ? ask.key : ask.deviceId;
    const result = sb.functions
      .invoke<{ route?: PublicRoute | null; problem?: string | null; error?: string }>('traffic-route', {
        body: { tenant_id: tenantId, source: ask.source, id },
      })
      .then(async ({ data, error }): Promise<PublicRouteResult> => {
        if (error) {
          // the function's own words when it refused (403, 404, 429)
          const ctx = (error as { context?: Response }).context;
          const said = ctx && typeof ctx.json === 'function' ? await ctx.json().catch(() => null) : null;
          return { route: null, problem: (said as { error?: string } | null)?.error ?? problemOf(error) };
        }
        return { route: data?.route ?? null, problem: data?.problem ?? null };
      })
      .catch((e: unknown): PublicRouteResult => ({ route: null, problem: problemOf(e) }));
    return { source: ask.source, result };
  };
}

/** The popup's content: the aircraft's label, the rows and the caption, as text nodes. */
export function routePopup(
  a: AircraftPosition,
  view: PublicRouteView,
  airlineWords: readonly string[],
  nowMs: number = Date.now(),
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'map-route-popup';
  box.appendChild(textNode(trackedAircraftLabel(a), 'label'));
  const rows = [
    ...aircraftSourceRows(a, nowMs),
    ...airlineWords.map((w) => ({ label: 'Route', value: w })),
    ...view.rows,
  ];
  if (rows.length > 0) {
    const dl = document.createElement('dl');
    for (const r of rows) {
      const row = document.createElement('div');
      row.className = 'map-identify__row';
      const dt = document.createElement('dt');
      dt.className = 'label';
      dt.textContent = r.label;
      const dd = document.createElement('dd');
      dd.className = 'data';
      dd.textContent = r.value;
      row.append(dt, dd);
      dl.appendChild(row);
    }
    box.appendChild(dl);
  }
  const caption =
    view.caption ??
    (view.line?.schedule ? 'Route: adsbdb (the airline’s published schedule, not this flight’s plan)' : null);
  if (caption) box.appendChild(textNode(caption, 'caption muted'));
  return box;
}

/** Draw a route's line into `group` (cleared first): the thin stroke, solid or dashed, its waypoints and label. */
export function drawRoute(
  group: L.LayerGroup,
  view: PublicRouteView,
  colour: string,
  o: { readonly renderer?: L.Renderer } = {},
): void {
  group.clearLayers();
  const line = view.line;
  if (!line || line.points.length < 2) return;
  const w = Number.parseFloat(css('map-stroke-thin')) || 1.5;
  group.addLayer(
    L.polyline(
      line.points.map((p) => [p.lat, p.lon] as L.LatLngTuple),
      {
        color: colour,
        weight: w,
        opacity: 1,
        interactive: false,
        className: 'map-route',
        ...(line.schedule ? { dashArray: `${w * 6} ${w * 4}` } : {}),
        ...(o.renderer ? { renderer: o.renderer } : {}),
      },
    ),
  );
  const dot = Number.parseFloat(css('map-trail-dot-radius')) || 3;
  for (const p of line.points) {
    if (!p.tip) continue;
    group.addLayer(
      L.circleMarker([p.lat, p.lon], {
        radius: dot,
        color: colour,
        weight: 0,
        fill: true,
        fillColor: colour,
        fillOpacity: 1,
        ...(o.renderer ? { renderer: o.renderer } : {}),
      }).bindTooltip(textNode(p.tip), { direction: 'top' }),
    );
  }
  if (line.label) {
    const mid = line.points[Math.floor(line.points.length / 2)]!;
    group.addLayer(
      L.tooltip({ permanent: true, direction: 'center', className: 'map-label map-route__label' })
        .setLatLng([mid.lat, mid.lon])
        .setContent(textNode(line.label)),
    );
  }
}

/**
 * A map's route: one at a time. `open` asks the aircraft's route once and shows the popup at the aircraft ("Asking
 * Oz Runways…" first), then the answer; closing the popup, or opening another, removes the line.
 */
export class RouteOverlay {
  private popup: L.Popup | null = null;
  private n = 0;

  constructor(
    private readonly map: L.Map,
    private readonly group: L.LayerGroup,
    private readonly offsetH: () => number,
  ) {}

  open(a: AircraftPosition, fetcher: RouteFetcher, colours: ReadonlyMap<string, AircraftColours>): boolean {
    // one icon of several sources: the public source with a route to ask, and every source's words (R238)
    const asked = fetcher(aircraftRouteTarget(a, (x) => publicRouteAsk(x) !== null));
    if (!asked) {
      if (!a.sources) return false;
      this.close();
      this.popup = L.popup({ className: 'map-route-popup__box', maxWidth: 360, autoClose: true })
        .setLatLng([a.lat, a.lng])
        .setContent(routePopup(a, { rows: [], caption: null, line: null }, []))
        .openOn(this.map);
      return true;
    }
    this.close();
    const n = ++this.n;
    const colour = colourOf(colours.get(a.id)?.icon ?? 'accent');
    const at = { lat: a.lat, lon: a.lng };
    const show = (view: PublicRouteView, airline: readonly string[]): void => {
      if (n !== this.n) return;
      drawRoute(this.group, view, colour);
      this.popup?.setContent(routePopup(a, view, airline));
    };
    const waiting = asked.source === 'airline' ? ['Asking Teslyn Cloud for the published schedule…'] : [];
    this.popup = L.popup({ className: 'map-route-popup__box', maxWidth: 360, autoClose: true })
      .setLatLng([a.lat, a.lng])
      .setContent(
        routePopup(a, publicRouteView('asking', asked.source, at, Date.now(), this.offsetH()), waiting),
      )
      .openOn(this.map);
    this.popup.on('remove', () => {
      if (n === this.n) {
        this.group.clearLayers();
        this.popup = null;
      }
    });
    void asked.result.then((result) => {
      const view = publicRouteView(result, asked.source, at, Date.now(), this.offsetH());
      const r = result.route;
      const airline =
        asked.source !== 'airline'
          ? []
          : r?.origin && r.destination
            ? [
                `${r.origin.name ?? r.origin.code} (${r.origin.code}) → ${r.destination.name ?? r.destination.code} (${r.destination.code})`,
              ]
            : ['No published schedule found for this flight.'];
      show(view, airline);
    });
    return true;
  }

  close(): void {
    this.n += 1;
    this.group.clearLayers();
    if (this.popup) {
      const p = this.popup;
      this.popup = null;
      this.map.closePopup(p);
    }
  }
}
